import { useTracking } from 'react-tracking';
import { RouterLink } from '@redux-first-router-link';

import { formatterCurrency } from '@/utilities/format';

import { Grid, Heading, Text } from '@simplywallst/ui-core';

import { CompanyIcon } from '@/components/CompanyIcon';

import { getTradingItemCurrencyISO } from '@/utilities/getTradingItemCurrencyISO';

import type { WatchlistItem } from '@/hooks/useWatchlist';
import type { Holding } from '@/hooks/portfolio/usePortfolio';

interface Props {
  company: WatchlistItem['company'] | Holding['company'];
}

export const MonitoringPanelHeaderLink = ({ company }: Props) => {
  const { trackEvent } = useTracking();
  const { canonicalURL, tickerSymbol, name, info } = company;

  const lastSharePrice = company.analysisValue?.lastSharePrice ?? null;

  const companyCurrencyISO =
    getTradingItemCurrencyISO(company.data.currencyInfo) ?? 'XXX';

  return (
    <RouterLink
      data-cy-id="monitoring-panel-header-link"
      noUnderline
      to={canonicalURL}
      onClick={() => {
        trackEvent({
          action: 'click',
          canonicalURL,
        });
      }}
    >
      <Grid templateColumns="max-content auto" gap="8px">
        <CompanyIcon
          website={info?.url}
          tickerSymbol={tickerSymbol}
          size={40}
        />
        <Grid rowGap="2px">
          <Grid
            templateColumns="repeat(2, min-content)"
            alignItems="center"
            gap="5px"
          >
            <Heading as="h2" typography="xSmallStrong" color="brand01">
              {tickerSymbol}
            </Heading>

            <Text typography="xSmall" data-cy-id="share-price">
              {formatterCurrency(lastSharePrice, companyCurrencyISO)}
            </Text>
          </Grid>
          <Text
            typography="tiny"
            color="whiteRGBA"
            opacity={0.5}
            lineClamp={1}
            data-cy-id="company-name"
          >
            {name}
          </Text>
        </Grid>
      </Grid>
    </RouterLink>
  );
};
